import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { IndexedDBService } from '../../../../services/indexed.db.service';
import { ActivatedRoute } from '@angular/router';
import { RfqIntakeService, FileUploadResponse } from '../../../../services/rfq.intake.service';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastrService } from 'ngx-toastr';

export enum FileType {
  Model3D = 'Model3D',
  Drawing2D = 'Drawing2D',
  Supporting = 'Supporting'
}
interface PartToEdit {
  partId: string;
  partName: string;
  quantity: string;
  material: string;
  grade: string;
  finish: string;
  tolerance: string;
  modelFile: boolean;
  drawingFile: boolean;
}

interface DrawingFile {
  name: string;
  file?: File;
  preview?: string;
  s3FileName?: string;
  s3FileUrl?: string;
}

interface ModelFile {
  name: string;
  file?: File;
  s3FileName?: string;
  s3FileUrl?: string;
}

// Interface for structured supporting files
interface SupportingFileEntry {
  name: string;
  file?: File;
  s3FileName?: string;
  s3FileUrl?: string;
}

interface RFQPartData {
  partDetails: {
    partName: string;
    partNumber: string;
    quantity: number;
    material: string;
    materialGrade: string;
    finishes: string[];
    tolerance: string;
    customTolerance?: string;
    notes: string;
  };
  files: {
    fileName: string;
    fileUrl: string;
    fileType: string;
  }[];
  showSummary: boolean;
  hasUploadedFiles: boolean;
}

// Custom validator: at least one non-empty finish
function atLeastOneFinishValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (Array.isArray(value) && value.some(f => !!f)) {
      return null;
    }
    return { required: true };
  };
}

@Component({
  selector: 'app-add-parts',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule,FormsModule, SelectModule,MultiSelectModule],
  templateUrl: './add-parts.component.html',
  styleUrl: './add-parts.component.scss'
})
export class AddPartsComponent implements  OnInit {
  readonly STANDARD_TOLERANCE = '+/- .005" (+/- 0.13mm)';
  
  
  @Input() partToEdit: PartToEdit | undefined;
  @Input() showSummary: boolean = false;
  @Input() hasUploadedFiles: boolean = false;
  rfqDetails: any;
  @Output() continueToSummary = new EventEmitter<RFQPartData>();
  @Output() parentEmitter = new EventEmitter<any>();
  private rfqId: string;
  private pendingS3Uploads: number = 0; // Added to track pending uploads
  // Allowed file extensions for 2D drawings
  readonly allowedDrawingExtensions = ['.pdf', '.dwg', '.dxf', '.slddrw', '.png', '.jpg', '.bmp', '.tif'];
  
  // Allowed file extensions for 3D models
  readonly allowedModelExtensions = ['.step', '.stp', '.stl', '.igs', '.iges', '.x_t', '.x_b', '.sldprt', '.3mf'];
  
  // Array to store uploaded drawing files
  drawingFiles: DrawingFile[] = [];
  
  // Array to store uploaded model files
  modelFiles: ModelFile[] = [];
  
  // Array to store uploaded supporting files (using the new interface)
  supportingFiles: SupportingFileEntry[] = []; 
  
  // Error message for invalid file types
  fileError: string = '';
  
  // Error message for invalid model file types
  modelFileError: string = '';

  // Reactive Form
  partForm!: FormGroup;

  // Material options
  materialList: any[] = []; 
  toleranceList: any[] = []; 
  postProductionList: any[] = [];
  // Finish options
  
  // Tolerance options
  toleranceOptions = [
    
    { value: '+/- .005" (+/- 0.13mm)', label: '+/- .005" (+/- 0.13mm)' },
    { value: 'custom', label: 'Custom' }
  ];

  submitted = false;
  

  constructor(private fb: FormBuilder, private indexedDBService: IndexedDBService, private route: ActivatedRoute, private rfqIntakeService: RfqIntakeService, private toastrService: ToastrService) {    
    this.rfqId = this.route.snapshot.params['rfqId'] || 'temp-rfq';
    this.initForm();
  }

  private initForm(): void {
    this.partForm = this.fb.group({
      rfqPartId: [null],
      partName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^(?!\s*$).+/)]],
      partNumber: ['', [Validators.required, Validators.minLength(1), Validators.pattern(/^(?!\s*$).+/)]],
      quantity: [, [Validators.required, Validators.min(1)]],
      material: ['', Validators.required],
      materialGrade: [''],
      finishes: [[]],
      tolerance: [this.STANDARD_TOLERANCE, Validators.required],
      customTolerance: [''],
      notes: ['']
    });

    // Subscribe to tolerance changes to handle custom tolerance
    this.partForm.get('tolerance')?.valueChanges.subscribe(value => {
      const customToleranceControl = this.partForm.get('customTolerance');
      if (value === 'custom') {
        customToleranceControl?.setValidators([Validators.required]);
      } else {
        customToleranceControl?.clearValidators();
        customToleranceControl?.setValue('');
      }
      customToleranceControl?.updateValueAndValidity();
    });
  }
 


  // Handle file selection
  onDrawingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.validateAndAddDrawingFile(file);
    }
  }

  // Handle drag and drop
  onDrawingFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer?.files) {
      const file = event.dataTransfer.files[0];
      this.validateAndAddDrawingFile(file);
    }
  }

  // Prevent default drag behaviors
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  // Validate and add drawing file
  private validateAndAddDrawingFile(file: File): void {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (this.allowedDrawingExtensions.includes(fileExtension)) {
      this.fileError = '';
      this.hasUploadedFiles = true; // Set this early, as an upload attempt is being made

      this.pendingS3Uploads++;
      this.rfqIntakeService.uploadFileToS3(file).subscribe({
        next: (response: FileUploadResponse) => {

          const drawingFile: DrawingFile = {
            name: file.name,
            file: file,
            s3FileName: response.data.fileName,
            s3FileUrl: response.data.fileUrl
          };

          // Generate preview for image files
          if (['.png', '.jpg', '.bmp', '.tif'].includes(fileExtension)) {
            const reader = new FileReader();
            reader.onload = (e: any) => {
              drawingFile.preview = e.target.result;
            };
            reader.readAsDataURL(file);
          }
          
          this.drawingFiles.push(drawingFile);
        },
        error: (error) => {
          console.error('Error uploading drawing file to S3:', error);
          // TODO: Add toastr error notification if needed
          // this.toastr.error('Drawing file upload failed.', 'Error');
          // Mark the file as failed or handle as per requirements
        },
        complete: () => {
          this.pendingS3Uploads--; // Decrement pending uploads when complete (success or error handled)
        }
      });
    } else {
      this.fileError = `Invalid file type. Allowed types: ${this.allowedDrawingExtensions.join(', ')}`;
    }
  }

  // Remove drawing file
  removeDrawingFile(index: number): void {
    this.drawingFiles.splice(index, 1);
    // Reset the file input to allow re-upload of the same file
    const fileInput = document.querySelector('#drawingFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    // Update the flag if no files are left
    if (this.drawingFiles.length === 0 && this.modelFiles.length === 0) {
      this.hasUploadedFiles = false;
    }
  }

  // Handle model file selection
  onModelFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      Array.from(input.files).forEach(file => this.validateAndAddModelFile(file));
    }
  }

  // Handle model file drag and drop
  onModelFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer?.files) {
      Array.from(event.dataTransfer.files).forEach(file => this.validateAndAddModelFile(file));
    }
  }

  // Validate and add model file
  private validateAndAddModelFile(file: File): void {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (this.allowedModelExtensions.includes(fileExtension)) {
      this.modelFileError = '';
      this.hasUploadedFiles = true; // Set this early, as an upload attempt is being made

      this.pendingS3Uploads++; // Increment pending uploads
      this.rfqIntakeService.uploadFileToS3(file).subscribe({
        next: (response: FileUploadResponse) => {

          const modelFile: ModelFile = {
            name: file.name,
            file: file,
            s3FileName: response.data.fileName,
            s3FileUrl: response.data.fileUrl
          };

          this.modelFiles.push(modelFile); // Add to array only on success

          // TODO: Add toastr success notification if needed
          // this.toastr.success('Model file uploaded and processed!', 'Success');
        },
        error: (error) => {
          console.error('Error uploading model file to S3:', error);
          // File is not added to this.modelFiles in this error case.
          // TODO: Add toastr error notification if needed
          // this.toastr.error('Model file S3 upload failed.', 'Error');
          // If hasUploadedFiles was set based on files in the list, you might need to re-evaluate it here
          // or manage it based on upload attempts vs successful uploads.
          // For simplicity, we set it true earlier, meaning an attempt was made.
        },
        complete: () => {
          this.pendingS3Uploads--; // Decrement pending uploads when complete
        }
      });

    } else {
      this.modelFileError = `Invalid file type. Allowed types: ${this.allowedModelExtensions.join(', ')}`;
    }
  }

  // Remove model file
  removeModelFile(index: number): void {
    this.modelFiles.splice(index, 1);
    // Reset the file input to allow re-upload of the same file
    const fileInput = document.querySelector('#modelFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    // Update the flag if no files are left
    if (this.modelFiles.length === 0 && this.drawingFiles.length === 0) {
      this.hasUploadedFiles = false;
    }
  }

  // Handle continue button click
  onContinueClick(): void {
    // Set the flag to show uploaded files section
    this.hasUploadedFiles = true;
    this.showEditPartSection=true;
  }

  // Helper method to check if a form control is invalid and touched
  isFieldInvalid(fieldName: string): boolean {
    const field = this.partForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  // Helper method to get error message for a field
  getErrorMessage(fieldName: string): string {
    const field = this.partForm.get(fieldName);
    if (!field) return '';

    if (field.hasError('required')) {
      return 'This field is required';
    }
    if (field.hasError('minlength')) {
      return `Minimum length is ${field.errors?.['minlength'].requiredLength} characters`;
    }
    if (field.hasError('min')) {
      return `Minimum value is ${field.errors?.['min'].min}`;
    }
    return '';
  }

  onEditPart(rfqPartId: number): void {
    this.showSummary = false;
    this.hasUploadedFiles = true;
    // Store the rfqPartId in the form
    this.partForm.patchValue({ rfqPartId: rfqPartId });
    this.patchPartData(rfqPartId);
  }

  onContinueToSummary(): void {
    if (this.pendingS3Uploads > 0) {
      return;
    }

    this.submitted = true;
    if (!this.partForm.valid) {
      Object.keys(this.partForm.controls).forEach(key => {
        const control = this.partForm.get(key);
        control?.markAsTouched();
      });
      return;
    }
    const formData = this.partForm.value;
    const existingRfqPartId = formData.rfqPartId; // Get the rfqPartId if it exists
    // Collect all 2D drawing file data
    const drawingFileData = this.drawingFiles
      .filter(df => df && df.s3FileName && df.s3FileUrl)
      .map(df => ({
        fileName: df.s3FileName!,
        fileUrl: df.s3FileUrl!,
        fileType: FileType.Drawing2D
      }));

    // Collect all 3D model file data
    const modelFileData = this.modelFiles
      .filter(mf => mf && mf.s3FileName && mf.s3FileUrl)
      .map(mf => ({
        fileName: mf.s3FileName!,
        fileUrl: mf.s3FileUrl!,
        fileType: FileType.Model3D
      }));

    // Collect all Supporting file data
    const supportingFileData = this.supportingFiles
      .filter(sf => sf && sf.s3FileName && sf.s3FileUrl)
      .map(sf => ({
        fileName: sf.s3FileName!,
        fileUrl: sf.s3FileUrl!,
        fileType: FileType.Supporting as string
      }));

    // Combine all file data arrays
    const fileData = [...drawingFileData, ...modelFileData, ...supportingFileData];

    const rfqPartData: RFQPartData = {
      partDetails: {
        partName: formData.partName,
        partNumber: formData.partNumber,
        quantity: formData.quantity,
        material: formData.material,
        materialGrade: formData.materialGrade,
        finishes: formData.finishes,
        tolerance: formData.tolerance === 'custom' ? formData.customTolerance : formData.tolerance,
        customTolerance: formData.tolerance === 'custom' ? formData.customTolerance : undefined,
        notes: formData.notes
      },
      files: fileData,
      showSummary: true,
      hasUploadedFiles: true
    };
    // Save to IndexedDB here!
    this.saveToIndexedDB(this.rfqId, rfqPartData, existingRfqPartId);
  }
  showEditPartSection=false;
  partDetailList:any[]=[];
  patchPartData(rfqPartId?: number) {
     this.indexedDBService.getRfqIntakeData(this.rfqId).subscribe({
      next: (record: any) => {
        if(record && record.rfqDetails){
          this.rfqDetails=record.rfqDetails;
        }
        this.partDetailList=[];
        if (record && record.partDetails && record.partDetails.length > 0) {
          this.partDetailList=record.partDetails;
          // If rfqPartId is provided, find that specific part, otherwise get the last part
          const partToEdit = rfqPartId 
            ? record.partDetails.find((part: any) => part.rfqPartId === rfqPartId)
            : record.partDetails[record.partDetails.length - 1];

          if (!partToEdit) {
            console.error('Part not found with rfqPartId:', rfqPartId);
            return;
          }
          
          // Check if the tolerance matches the standard tolerance
          const isStandardTolerance = partToEdit.tolerance === this.STANDARD_TOLERANCE;
          
          // Patch the form with the part details
          this.partForm.patchValue({
            rfqPartId: partToEdit.rfqPartId,
            partName: partToEdit.partName,
            partNumber: partToEdit.partNumber,
            quantity: partToEdit.quantity,
            material: partToEdit.material,
            materialGrade: partToEdit.materialGrade,
            finishes: Array.isArray(partToEdit.finishes)
              ? partToEdit.finishes
              : typeof partToEdit.finishes === 'string'
                ? partToEdit.finishes.split(',').map((f: string) => f.trim())
                : [],
            tolerance: isStandardTolerance ? this.STANDARD_TOLERANCE : 'custom',
            customTolerance: isStandardTolerance ? '' : partToEdit.tolerance,
            notes: partToEdit.notes
          });

          // Store existing files temporarily
          const existingModelFiles = [...this.modelFiles];
          const existingDrawingFiles = [...this.drawingFiles];
          const existingSupportingFiles = [...this.supportingFiles];

          // Initialize file arrays
          this.modelFiles =[];
          this.drawingFiles = [];
          this.supportingFiles = [];
          this.showEditPartSection=false;
          if( partToEdit.rfqPartId === rfqPartId){
            this.showEditPartSection=true;
          }
          console.log('partToEdit.rfqPartId === rfqPartId',partToEdit.rfqPartId, rfqPartId);
          this.hasUploadedFiles = true;

          // Restore files if they exist in lastPart
          if (partToEdit.files && Array.isArray(partToEdit.files)) {
            partToEdit.files.forEach((dbFile: any) => {
              if (!dbFile.fileName || !dbFile.fileUrl || !dbFile.fileType) {
                console.warn('Skipping file from DB due to missing properties:', dbFile);
                return;
              }

              switch (dbFile.fileType) {
                case FileType.Model3D:
                  // Check if this file already exists in our current modelFiles
                  const existingModelFile = existingModelFiles.find(
                    mf => mf.s3FileName === dbFile.fileName || mf.name === dbFile.fileName
                  );
                  
                  const modelFile: ModelFile = {
                    name: dbFile.fileName,
                    s3FileName: dbFile.fileName,
                    s3FileUrl: dbFile.fileUrl,
                    // Preserve the File object if it exists in current modelFiles
                    file: existingModelFile?.file
                  };
                  this.modelFiles.push(modelFile);
                  break;

                case FileType.Drawing2D:
                  const existingDrawingFile = existingDrawingFiles.find(
                    df => df.s3FileName === dbFile.fileName || df.name === dbFile.fileName
                  );
                  
                  let previewUrl: string | undefined = undefined;
                  const extension = dbFile.fileName.split('.').pop()?.toLowerCase();
                  if (extension && ['.png', '.jpg', '.jpeg', '.bmp', '.tif', '.gif'].includes('.' + extension)) {
                    previewUrl = dbFile.fileUrl;
                  }

                  const drawingFile: DrawingFile = {
                    name: dbFile.fileName,
                    s3FileName: dbFile.fileName,
                    s3FileUrl: dbFile.fileUrl,
                    file: existingDrawingFile?.file,
                    preview: existingDrawingFile?.preview || previewUrl
                  };
                  this.drawingFiles.push(drawingFile);
                  break;

                case FileType.Supporting:
                  const existingSupportingFile = existingSupportingFiles.find(
                    sf => sf.s3FileName === dbFile.fileName || sf.name === dbFile.fileName
                  );
                  
                  const supportingFile: SupportingFileEntry = {
                    name: dbFile.fileName,
                    s3FileName: dbFile.fileName,
                    s3FileUrl: dbFile.fileUrl,
                    file: existingSupportingFile?.file
                  };
                  this.supportingFiles.push(supportingFile);
                  break;

                default:
                  console.warn('Unknown fileType from DB:', dbFile.fileType, dbFile);
              }
            });

            // Set hasUploadedFiles based on whether we have any files
            this.hasUploadedFiles = this.modelFiles.length > 0 || 
                                  this.drawingFiles.length > 0 || 
                                  this.supportingFiles.length > 0;
          }
        } else {
          // No partDetails in record or record is empty
          this.showSummary = false;
          this.hasUploadedFiles = false;
          // Clear any existing form data if needed, or ensure initForm() sets defaults
          this.initForm(); // Re-initialize form for a clean state
          this.openAddPartModal();
        }
      },
      error: (err: any) => {
        console.error('Error loading data from IndexedDB for patching:', err);
        // Consider falling back to a clean state
        this.initForm();
        this.openAddPartModal();
      }
    });
  }

  saveToIndexedDB(rfqId: string, data: any, existingRfqPartId?: number) {
    this.indexedDBService.getRfqIntakeData(this.rfqId).subscribe({
      next: (existing: any) => {
        // If we have an existing rfqPartId, use it, otherwise calculate a new one
        let nextRfqPartId = existingRfqPartId;
        if (!nextRfqPartId && existing?.partDetails && existing.partDetails.length > 0) {
          // Find the maximum rfqPartId and add 1
          nextRfqPartId = Math.max(...existing.partDetails.map((part: any) => part.rfqPartId || 0)) + 1;
        } else if (!nextRfqPartId) {
          nextRfqPartId = 1;
        }

        // Build your new part object
        const newPart = {
          rfqPartId: nextRfqPartId,
          partName: data.partDetails.partName,
          partNumber: data.partDetails.partNumber,
          quantity: data.partDetails.quantity,
          material: data.partDetails.material,
          materialGrade: data.partDetails.materialGrade,
          finishes: data.partDetails.finishes,
          tolerance: data.partDetails.tolerance,
          notes: data.partDetails.notes,
          files: data.files
        };

        let updatedPartDetails;
        if (existing?.partDetails) {
          if (existingRfqPartId) {
            // If we have an existing rfqPartId, find and update that specific part
            const existingPartIndex = existing.partDetails.findIndex(
              (part: any) => part.rfqPartId === existingRfqPartId
            );
            if (existingPartIndex !== -1) {
              updatedPartDetails = [...existing.partDetails];
              updatedPartDetails[existingPartIndex] = newPart;
            } else {
              // If somehow we can't find the part with the ID, append as new
              updatedPartDetails = [...existing.partDetails, newPart];
            }
          } else {
            // No existing rfqPartId, append as new part
            updatedPartDetails = [...existing.partDetails, newPart];
          }
        } else {
          // If no existing parts, create new array with this part
          updatedPartDetails = [newPart];
        }

        const updated = {
          ...existing,
          rfqId: rfqId,
          rfqDetails: existing?.rfqDetails || {}, // preserve rfqDetails if any
          partDetails: updatedPartDetails
        };

        this.indexedDBService.saveRfqIntakeData(updated).subscribe({
          next: () => {
            this.showSummary = true;
            this.showSummary = true;
            this.hasUploadedFiles = true;
            this.showEditPartSection=false;
            this.patchPartData();
          },
          error: (err: any) => console.error('IndexedDB save error:', err)
        });
      },
      error: (err: any) => console.error('IndexedDB load error:', err)
    });
  }

  ngOnInit() {
    this.getPartDetailDropdowns();
    console.log('partToEdit',this.partToEdit);
    if(this.partToEdit){  
      this.patchPartData(Number(this.partToEdit));
    }else{
      this.patchPartData();
    }
  }

  // Update the button click handler in the template
  onReviewSummaryClick(): void {
    if(this.partDetailList.length > 0){
    this.parentEmitter.emit();
    }
  }

  getPartDetailDropdowns() {
    this.rfqIntakeService.getRfqDetailDropdowns().subscribe((res: any) => {
      if (res.status && res.data) {
        res.data.forEach((item: any) => {
          switch (item.masterKey) {
            case 'materials':
              this.materialList = item.masterValue;
              break;
            case 'tolerances':
              this.toleranceList = item.masterValue;
              break;
            case 'postProduction':
              this.postProductionList = item.masterValue;
              break;
          
          }
        });
      }
    });
  }

  onSupportingFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const newFiles = Array.from(input.files);

      newFiles.forEach(file => {
        // Optional: Prevent adding duplicates based on name, if desired, before upload attempt
        // if (this.supportingFiles.some(sf => sf.name === file.name)) {
        //   console.log(`Supporting file "${file.name}" already exists or is being uploaded.`);
        //   return;
        // }

        this.hasUploadedFiles = true; // An upload attempt is being made
        this.pendingS3Uploads++;
        this.rfqIntakeService.uploadFileToS3(file).subscribe({
          next: (response: FileUploadResponse) => {
            const supportingFileEntry: SupportingFileEntry = {
              name: file.name,
              file: file,
              s3FileName: response.data.fileName,
              s3FileUrl: response.data.fileUrl
            };
            this.supportingFiles.push(supportingFileEntry);
            // TODO: Add toastr success notification if needed
          },
          error: (err) => {
            console.error('Error uploading supporting file to S3:', err);
            // TODO: Add toastr error notification if needed
            // Since the file isn't added to this.supportingFiles on error, no removal needed from this list.
          },
          complete: () => {
          
            this.pendingS3Uploads--;
            // If all files (model, drawing, supporting) are removed and this was the last upload,
            // update hasUploadedFiles accordingly.
            if (this.pendingS3Uploads === 0 && this.modelFiles.length === 0 && this.drawingFiles.length === 0 && this.supportingFiles.length === 0) {
              console.log("this.hasUploadedFiles@",this.hasUploadedFiles);
              this.hasUploadedFiles = false;
            }
          }
        });
      });
    }
    if (input) {
      input.value = ''; // Reset the file input
    }
  }

  removeSupportingFile(index: number): void {
    if (this.supportingFiles[index]) {
      // Optional: If file was uploaded to S3, you might want to call an API to delete it from S3.
      // const fileToRemove = this.supportingFiles[index];
      // if (fileToRemove.s3FileUrl) { /* call S3 delete service */ }
      this.supportingFiles.splice(index, 1);
    }
    if (this.modelFiles.length === 0 && this.drawingFiles.length === 0 && this.supportingFiles.length === 0) {
      this.hasUploadedFiles = false;
    }
  }

  openAddPartModal() {
    const modalElement = document.getElementById('addPartModal');
    if (modalElement) {
      // @ts-ignore
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  /**
   * Formats the file name to remove the prefix and show only the relevant part
   * @param fileName The full file name
   * @returns The formatted file name
   */
  formatFileName(fileName: string): string {
    if (!fileName) return '';
    
    // Find the index of 'S3_' in the filename
    const s3Index = fileName.indexOf('S3_');
    if (s3Index !== -1) {
      // Return everything after 'S3_'
      return fileName.substring(s3Index + 3);
    }
    
    // If 'S3_' is not found, return the original filename
    return fileName;
  }
  goBackToRfqDetails() {
    this.parentEmitter.emit({backToRfqDetails:true});
  }
  backCloseFileUploadModel() {
    console.log('backCloseFileUploadModel');
    this.parentEmitter.emit({backToRfqDetails:true});
  }
}
